export interface NutritionRow {
  label: string;
  per100g: string;
  dv?: string;
}

export interface Ingredient {
  name: string;
  benefit: string;
  icon: string;
}

export interface Certification {
  name: string;
  description: string;
}

export interface PurchaseLink {
  platform: string;
  url: string;
  type: 'quickcommerce' | 'ecommerce';
  logo: string;
  tagline: string;
  color: string;
}

export interface ProductDetail {
  id: string;
  name: string;
  tagline: string;
  description: string;
  longDescription: string;
  image: string;
  alt: string;
  badge?: string;
  badgeColor?: string;
  accent: string;
  textAccent: string;
  nutritionFacts: NutritionRow[];
  ingredients: Ingredient[];
  ingredientsList: string;
  certifications: Certification[];
  purchaseLinks: PurchaseLink[];
  highlights: string[];
  packSizes: string[];
}

export const productData: Record<string, ProductDetail> = {
  'chocolate-chhatua': {
    id: 'chocolate-chhatua',
    name: 'Chocolate Chhatua',
    tagline: 'Kids love it. Parents approve it.',
    description: 'Ideal breakfast option packed with multi-grains and chocolate powder.',
    longDescription: 'Kasturi Chocolate Chhatua is a delicious, nutritious breakfast blend crafted for children and families. Made from wheat, chuda, chana and nuts, blended with chocolate powder — it delivers real nutrition in a flavour kids actually want. No artificial colours, no preservatives, just wholesome goodness in every spoonful.',
    image: '/assets/images/product_images/chocolate-chhatua.jpg',
    alt: 'Kasturi Chocolate Chhatua pack with chocolate powder and multi-grain ingredients',
    badge: 'Kids Favourite',
    badgeColor: '#E8A838',
    accent: '#3D1F0A',
    textAccent: '#E8A838',
    nutritionFacts: [
      { label: 'Energy', per100g: '310 kcal', dv: '16%' },
      { label: 'Total Fat', per100g: '7g', dv: '11%' },
      { label: 'Total Carbohydrates', per100g: '75g', dv: '25%' },
      { label: 'Dietary Fibre', per100g: '1.8g', dv: '7%' },
      { label: 'Total Sugars', per100g: '15g' },
      { label: 'Protein', per100g: '11g', dv: '22%' },
    ],
    ingredients: [
      { name: 'Wheat', benefit: 'Complex carbs for sustained energy', icon: '🌾' },
      { name: 'Chuda (Flattened Rice)', benefit: 'Light, easily digestible carbohydrates', icon: '🍚' },
      { name: 'Chana (Bengal Gram)', benefit: 'Plant-based protein source', icon: '🫘' },
      { name: 'Peanut', benefit: 'Healthy fats & protein', icon: '🥜' },
      { name: 'Yellow Pea', benefit: 'Additional plant protein & fibre', icon: '🟢' },
      { name: 'Cashew', benefit: 'Rich, creamy texture & minerals', icon: '🌰' },
      { name: 'Almond', benefit: 'Vitamin E & healthy fats', icon: '🌰' },
      { name: 'Milk Powder', benefit: 'Creaminess & added calcium', icon: '🥛' },
      { name: 'Chocolate Powder', benefit: 'Real chocolate flavour kids love', icon: '🍫' },
    ],
    ingredientsList: 'Wheat, Chuda (Flattened Rice), Chana (Bengal Gram), Peanut, Yellow Pea, Cashew, Almond, Sugar, Milk Powder, Chocolate Powder. Contains: Wheat (Gluten), Peanut, Tree Nuts (Cashew, Almond), Milk.',
    certifications: [
      { name: 'FSSAI Licensed', description: 'Manufactured under FSSAI License No. 10019022000858' },
      { name: 'No Artificial Colours', description: 'Free from synthetic food dyes and artificial colouring agents' },
      { name: 'No Preservatives', description: 'Contains zero chemical preservatives — naturally shelf-stable' },
      { name: 'Clean Label', description: 'Every ingredient is declared on the pack — no hidden additives' },
    ],
    purchaseLinks: [
      { platform: 'Amazon', url: 'https://amzn.in/d/01t7DWeJ', type: 'ecommerce', logo: '/assets/images/Amazon_icon.png', tagline: 'Delivered in 2–5 days', color: '#FF9900' },
      { platform: 'Flipkart', url: 'https://dl.flipkart.com/s/Gd!DT1uuuN', type: 'ecommerce', logo: '/assets/images/flipkart-com-logo.png', tagline: 'Free delivery on ₹499+', color: '#2874F0' },
    ],
    highlights: ['Multi-grain & nut blend', 'Real chocolate flavour', 'No artificial colours', 'Kid-approved taste', 'Ready in 2 minutes'],
    packSizes: ['200g', '500g', '1kg'],
  },
  'mix-chhatua': {
    id: 'mix-chhatua',
    name: 'Mix Chhatua',
    tagline: 'Wholesome. Balanced. Daily.',
    description: 'Balance your nutritional and lifestyle demands with the wholesomeness of multi-grains.',
    longDescription: 'Kasturi Mix Chhatua is the everyday superfood blend rooted in Odia tradition. A carefully balanced mix of wheat, chuda, chana, yellow pea and nuts, it provides complete nutrition for the whole family. Eaten as a porridge, mixed with milk, or stirred into water with a pinch of salt — it adapts to your lifestyle while delivering consistent nourishment.',
    image: '/assets/images/product_images/mix-chhatua.jpg',
    alt: 'Kasturi Mix Chhatua multi-grain product pack',
    accent: '#1A0F05',
    textAccent: '#C17B2F',
    nutritionFacts: [
      { label: 'Energy', per100g: '310 kcal', dv: '16%' },
      { label: 'Total Fat', per100g: '7g', dv: '11%' },
      { label: 'Total Carbohydrates', per100g: '75g', dv: '25%' },
      { label: 'Dietary Fibre', per100g: '1.8g', dv: '7%' },
      { label: 'Total Sugars', per100g: '15g' },
      { label: 'Protein', per100g: '11g', dv: '22%' },
    ],
    ingredients: [
      { name: 'Wheat', benefit: 'Complex carbs for sustained energy', icon: '🌾' },
      { name: 'Chuda (Flattened Rice)', benefit: 'Light, easily digestible carbohydrates', icon: '🍚' },
      { name: 'Chana (Bengal Gram)', benefit: 'Plant-based protein source', icon: '🫘' },
      { name: 'Yellow Pea', benefit: 'Additional plant protein & fibre', icon: '🟢' },
      { name: 'Peanut', benefit: 'Healthy fats & protein', icon: '🥜' },
      { name: 'Cashew', benefit: 'Rich minerals & healthy fats', icon: '🌰' },
      { name: 'Almond', benefit: 'Vitamin E & healthy fats', icon: '🌰' },
      { name: 'Milk Powder', benefit: 'Creaminess & added calcium', icon: '🥛' },
    ],
    ingredientsList: 'Wheat, Chuda (Flattened Rice), Chana (Bengal Gram), Yellow Pea, Peanut, Cashew, Almond, Sugar, Milk Powder. Contains: Wheat (Gluten), Peanut, Tree Nuts (Cashew, Almond), Milk.',
    certifications: [
      { name: 'FSSAI Licensed', description: 'Manufactured under FSSAI License No. 10019022000858' },
      { name: 'No Preservatives', description: 'Contains zero chemical preservatives' },
      { name: 'Clean Label', description: 'Every ingredient is declared on the pack' },
    ],
    purchaseLinks: [
      { platform: 'Blinkit', url: 'https://blinkit.com/prn/x/prid/717541', type: 'quickcommerce', logo: '/assets/images/blinkit.png', tagline: '10-minute delivery', color: '#F8CB46' },
      { platform: 'Amazon', url: 'https://amzn.in/d/09w1xGZI', type: 'ecommerce', logo: '/assets/images/Amazon_icon.png', tagline: 'Delivered in 2–5 days', color: '#FF9900' },
      { platform: 'Flipkart', url: 'https://dl.flipkart.com/s/Gd2ZgJuuuN', type: 'ecommerce', logo: '/assets/images/flipkart-com-logo.png', tagline: 'Free delivery on ₹499+', color: '#2874F0' },
    ],
    highlights: ['Wholesome grain & nut blend', 'Traditional recipe', 'Versatile preparation', 'Whole family nutrition'],
    packSizes: ['200g', '500g', '1kg'],
  },
  'sugar-free-chhatua': {
    id: 'sugar-free-chhatua',
    name: 'Sugar-Free Chhatua',
    tagline: 'Zero added sugar. Full nutrition.',
    description: 'For health-conscious households that refuse to compromise on taste.',
    longDescription: 'Kasturi Sugar-Free Chhatua is crafted for diabetics, weight-watchers, and anyone who wants to eat clean without sacrificing flavour. With 13g of protein per 100g and absolutely zero added sugar, it is the smart choice for health-first households. The natural sweetness from roasted grains means you never feel deprived.',
    image: '/assets/images/product_images/sugar-free-chhatua.jpg',
    alt: 'Kasturi Sugar-Free Chhatua pack with zero added sugar label',
    badge: 'Sugar Free',
    badgeColor: '#4CAF50',
    accent: '#0D1A0A',
    textAccent: '#7BC67E',
    nutritionFacts: [
      { label: 'Energy', per100g: '293 kcal', dv: '15%' },
      { label: 'Total Fat', per100g: '8g', dv: '12%' },
      { label: 'Total Carbohydrates', per100g: '73g', dv: '24%' },
      { label: 'Added Sugars', per100g: '0g', dv: '0%' },
      { label: 'Protein', per100g: '13g', dv: '26%' },
    ],
    ingredients: [
      { name: 'Wheat', benefit: 'Complex carbs for sustained energy', icon: '🌾' },
      { name: 'Chuda (Flattened Rice)', benefit: 'Light, easily digestible carbohydrates', icon: '🍚' },
      { name: 'Chana (Bengal Gram)', benefit: 'High protein, low glycaemic index', icon: '🫘' },
      { name: 'Yellow Pea', benefit: 'Additional plant protein & fibre', icon: '🟢' },
      { name: 'Peanut', benefit: 'Healthy fats & protein', icon: '🥜' },
      { name: 'Almond', benefit: 'Vitamin E & healthy fats', icon: '🌰' },
    ],
    ingredientsList: 'Wheat, Chuda (Flattened Rice), Chana (Bengal Gram), Yellow Pea, Peanut, Almond. Contains: Wheat (Gluten), Peanut, Tree Nuts (Almond). No Added Sugar.',
    certifications: [
      { name: 'FSSAI Licensed', description: 'Manufactured under FSSAI License No. 10019022000858' },
      { name: 'Zero Added Sugar', description: 'Certified zero added sugar — suitable for diabetics' },
      { name: 'Diabetic Friendly', description: 'Low glycaemic index blend for blood sugar management' },
      { name: 'No Preservatives', description: 'Contains zero chemical preservatives' },
      { name: 'Clean Label', description: 'Every ingredient is declared on the pack' },
    ],
    purchaseLinks: [
      { platform: 'Amazon', url: 'https://amzn.in/d/08vjOqvA', type: 'ecommerce', logo: '/assets/images/Amazon_icon.png', tagline: 'Delivered in 2–5 days', color: '#FF9900' },
      { platform: 'Flipkart', url: 'https://dl.flipkart.com/s/1CFIh0NNNN', type: 'ecommerce', logo: '/assets/images/flipkart-com-logo.png', tagline: 'Free delivery on ₹499+', color: '#2874F0' },
    ],
    highlights: ['Zero added sugar', '13g protein per 100g', 'Diabetic friendly', 'Low GI blend', 'No artificial sweeteners'],
    packSizes: ['200g', '500g', '1kg'],
  },
  'chana-sattu': {
    id: 'chana-sattu',
    name: 'Chana Sattu',
    tagline: 'Ancient protein. Modern kitchen.',
    description: 'Smooth, lump-free batter — the perfect choice for breakfast. 20g protein per 100g.',
    longDescription: 'Kasturi Chana Sattu is cold-milled from premium roasted Bengal gram, preserving every nutrient through a traditional process. With 20g of plant protein per 100g and 12g of dietary fibre, it is one of the most nutrient-dense foods in Indian cuisine. Drink it as a sattu sharbat, make litti, or use it as a high-protein flour substitute.',
    image: '/assets/images/product_images/chana-sattu.jpg',
    alt: 'Kasturi Chana Sattu pack showing high protein content with roasted gram imagery',
    badge: 'High Protein',
    badgeColor: '#E8A838',
    accent: '#2A1500',
    textAccent: '#E8A838',
    nutritionFacts: [
      { label: 'Energy', per100g: '400 kcal', dv: '20%' },
      { label: 'Total Fat', per100g: '3g', dv: '5%' },
      { label: 'Total Carbohydrates', per100g: '64g', dv: '21%' },
      { label: 'Dietary Fibre', per100g: '12g', dv: '48%' },
      { label: 'Added Sugars', per100g: '0g', dv: '0%' },
      { label: 'Protein', per100g: '20g', dv: '40%' },
    ],
    ingredients: [
      { name: 'Roasted Bengal Gram', benefit: '20g protein per 100g — highest plant protein', icon: '🫘' },
    ],
    ingredientsList: '100% Roasted Bengal Gram (Chana). Single ingredient. No additives. No preservatives.',
    certifications: [
      { name: 'FSSAI Licensed', description: 'Manufactured under FSSAI License No. 10019022000858' },
      { name: 'Single Ingredient', description: '100% pure roasted Bengal gram — nothing else added' },
      { name: 'No Preservatives', description: 'Contains zero chemical preservatives' },
      { name: 'Vegan', description: 'Completely plant-based, suitable for vegans' },
      { name: 'Gluten Free', description: 'Naturally gluten-free — safe for gluten-sensitive individuals' },
    ],
    purchaseLinks: [
      { platform: 'Amazon', url: 'https://amzn.in/d/04fGO9pE', type: 'ecommerce', logo: '/assets/images/Amazon_icon.png', tagline: 'Delivered in 2–5 days', color: '#FF9900' },
      { platform: 'Flipkart', url: 'https://dl.flipkart.com/s/GdIvQHuuuN', type: 'ecommerce', logo: '/assets/images/flipkart-com-logo.png', tagline: 'Free delivery on ₹499+', color: '#2874F0' },
    ],
    highlights: ['20g protein per 100g', 'Single ingredient', 'Gluten free', 'Vegan', '12g dietary fibre'],
    packSizes: ['500g', '1kg', '2kg'],
  },
  'chakki-atta': {
    id: 'chakki-atta',
    name: 'Select Chakki Atta',
    tagline: 'Fortified. Fresh. Fundamental.',
    description: 'Essential micronutrients — Iron, Folic Acid, Vitamin B12 — in every roti.',
    longDescription: 'Kasturi Select Chakki Atta is stone-ground whole wheat flour fortified with Iron, Folic Acid, and Vitamin B12 as per FSSAI fortification standards. Made from premium wheat varieties, it delivers the soft texture and earthy flavour of traditional chakki atta while addressing common micronutrient deficiencies. Every roti becomes a nutritional statement.',
    image: '/assets/images/product_images/chakki-atta.jpg',
    alt: 'Kasturi Select Chakki Atta bag with freshly milled wheat flour',
    badge: 'Fortified',
    badgeColor: '#C17B2F',
    accent: '#1C1005',
    textAccent: '#D4A96A',
    nutritionFacts: [
      { label: 'Energy', per100g: '360 kcal', dv: '18%' },
      { label: 'Total Fat', per100g: '1.7g', dv: '3%' },
      { label: 'Saturated Fat', per100g: '0.3g', dv: '2%' },
      { label: 'Trans Fat', per100g: '0g' },
      { label: 'Total Carbohydrates', per100g: '72g', dv: '24%' },
      { label: 'Dietary Fibre', per100g: '13.6g', dv: '54%' },
      { label: 'Total Sugars', per100g: '1.5g' },
      { label: 'Added Sugars', per100g: '0g', dv: '0%' },
      { label: 'Protein', per100g: '11.25g', dv: '23%' },
      { label: 'Sodium', per100g: '2mg', dv: '0%' },
      { label: 'Iron', per100g: '14mg', dv: '78%' },
      { label: 'Folic Acid', per100g: '100mcg', dv: '25%' },
      { label: 'Vitamin B12', per100g: '0.5mcg', dv: '21%' },
      { label: 'Cholesterol', per100g: '0mg', dv: '0%' },
    ],
    ingredients: [
      { name: 'Whole Wheat', benefit: 'High fibre, complex carbohydrates', icon: '🌾' },
      { name: 'Iron (Ferrous Fumarate)', benefit: 'Prevents anaemia, supports oxygen transport', icon: '💪' },
      { name: 'Folic Acid', benefit: 'Essential for cell growth and DNA synthesis', icon: '🧬' },
      { name: 'Vitamin B12', benefit: 'Nerve function and red blood cell formation', icon: '⚡' },
    ],
    ingredientsList: 'Whole Wheat Flour (100%), Fortified with: Iron (as Ferrous Fumarate) 14mg/100g, Folic Acid 100mcg/100g, Vitamin B12 0.5mcg/100g. Contains: Wheat (Gluten).',
    certifications: [
      { name: 'FSSAI Licensed', description: 'Manufactured under FSSAI License No. 10019022000858' },
      { name: 'FSSAI Fortified', description: 'Fortified as per FSSAI Food Fortification Standards' },
      { name: 'Cholesterol Free', description: 'Zero cholesterol — heart-healthy choice' },
      { name: 'No Preservatives', description: 'Contains zero chemical preservatives' },
      { name: 'Stone Ground', description: 'Traditional chakki milling preserves natural nutrients' },
    ],
    purchaseLinks: [
      { platform: 'Amazon', url: 'https://www.amazon.in/s?srs=93889452031', type: 'ecommerce', logo: '/assets/images/Amazon_icon.png', tagline: 'Delivered in 2–5 days', color: '#FF9900' },
      { platform: 'Flipkart', url: 'https://www.flipkart.com/search?q=kasturi+atta', type: 'ecommerce', logo: '/assets/images/flipkart-com-logo.png', tagline: 'Free delivery on ₹499+', color: '#2874F0' },
    ],
    highlights: ['FSSAI Fortified', 'Iron + Folic Acid + B12', 'Zero cholesterol', 'High dietary fibre', 'Stone ground'],
    packSizes: ['1kg', '2kg', '5kg', '10kg'],
  },
  'ragi-flour': {
    id: 'ragi-flour',
    name: 'Ragi Flour',
    tagline: 'Calcium-rich. Bone-strong.',
    description: 'Wholesome finger millet flour packed with calcium, iron, and dietary fibre.',
    longDescription: 'Kasturi Ragi Flour is milled from premium finger millet (Eleusine coracana) — naturally one of the most calcium-rich plant foods available. Use it for rotis, dosas, porridge, or baked goods. A single daily serving meaningfully contributes to bone health, especially for children and the elderly.',
    image: '/assets/images/product_images/ragi-flour.jpg',
    alt: 'Kasturi Ragi Flour 500g pack showing finger millet grain and nutritional information',
    badge: 'New',
    badgeColor: '#E8A838',
    accent: '#0F0A02',
    textAccent: '#C17B2F',
    nutritionFacts: [
      { label: 'Energy', per100g: '382 kcal', dv: '19%' },
      { label: 'Total Fat', per100g: '4.3g', dv: '7%' },
      { label: 'Total Carbohydrates', per100g: '75g', dv: '25%' },
      { label: 'Dietary Fibre', per100g: '3.5g', dv: '14%' },
      { label: 'Protein', per100g: '11g', dv: '22%' },
    ],
    ingredients: [
      { name: 'Finger Millet (Ragi)', benefit: 'Naturally rich in calcium — supports bone strength', icon: '🌱' },
    ],
    ingredientsList: '100% Finger Millet Flour (Ragi / Eleusine coracana). Single ingredient. No additives. No preservatives. Naturally Gluten Free.',
    certifications: [
      { name: 'FSSAI Licensed', description: 'Manufactured under FSSAI License No. 10019022000858' },
      { name: 'Single Ingredient', description: '100% pure finger millet — nothing else added' },
      { name: 'Gluten Free', description: 'Naturally gluten-free — safe for celiac and gluten-sensitive individuals' },
      { name: 'Vegan', description: 'Completely plant-based, suitable for vegans' },
      { name: 'No Preservatives', description: 'Contains zero chemical preservatives' },
    ],
    purchaseLinks: [
      { platform: 'Blinkit', url: 'https://blinkit.com/prn/kasturi-ragi-flour/prid/1403219', type: 'quickcommerce', logo: '/assets/images/blinkit.png', tagline: '10-minute delivery', color: '#F8CB46' },
      { platform: 'Amazon', url: 'https://amzn.in/d/0bMFS5Zp', type: 'ecommerce', logo: '/assets/images/Amazon_icon.png', tagline: 'Delivered in 2–5 days', color: '#FF9900' },
      { platform: 'Flipkart', url: 'https://dl.flipkart.com/s/1C!LHGNNNN', type: 'ecommerce', logo: '/assets/images/flipkart-com-logo.png', tagline: 'Free delivery on ₹499+', color: '#2874F0' },
    ],
    highlights: ['Naturally rich in calcium', 'Single ingredient', 'Gluten free', 'Vegan', 'Bone health support'],
    packSizes: ['500g', '1kg'],
  },
};
